import type { TenantPerf } from './performance';

/**
 * Vue d'ensemble réseau Super Admin (parité legacy sa-overview) — logique PURE.
 * MRR, CA réseau, restos actifs/à relancer, classement, et export Odoo CSV.
 * Modèle Lil & Co : abonnement 99 €/mois par resto actif + commission 0,99 € par
 * commande payée en ligne.
 */
export const SUBSCRIPTION_CENTS = 9900; // 99 € / mois
export const COMMISSION_CENTS = 99; // 0,99 € / commande en ligne

export type ParcStatus = 'actif' | 'en veille' | 'à relancer';

/** Statut d'un resto selon ses commandes sur la fenêtre (seuils legacy). */
export function parcStatus(orders: number): ParcStatus {
  if (orders >= 15) return 'actif';
  if (orders >= 5) return 'en veille';
  return 'à relancer';
}

export interface NetworkKpis {
  restaurants: number;
  activeBilling: number;
  operational: number; // restos avec ≥ 1 commande sur la période
  toRelance: number; // restos < 5 commandes
  networkRevenueCents: number; // CA cumulé des restos
  onlineOrders: number;
  mrrCents: number; // abonnements + commissions
  arrCents: number; // MRR × 12
}

/** Calcule les KPIs réseau. Pur. */
export function networkKpis(tenants: TenantPerf[], activeBilling: number): NetworkKpis {
  const networkRevenueCents = tenants.reduce((s, t) => s + t.revenueCents, 0);
  const onlineOrders = tenants.reduce((s, t) => s + t.onlineOrders, 0);
  const operational = tenants.filter((t) => t.orders > 0).length;
  const toRelance = tenants.filter((t) => t.orders < 5).length;
  const mrrCents = activeBilling * SUBSCRIPTION_CENTS + onlineOrders * COMMISSION_CENTS;
  return {
    restaurants: tenants.length,
    activeBilling,
    operational,
    toRelance,
    networkRevenueCents,
    onlineOrders,
    mrrCents,
    arrCents: mrrCents * 12,
  };
}

/** Classement des restos par CA décroissant. Pur (ne mute pas l'entrée). */
export function rankByRevenue(tenants: TenantPerf[]): TenantPerf[] {
  return [...tenants].sort((a, b) => b.revenueCents - a.revenueCents);
}

/** Restos à relancer (commandes < seuil). Pur. */
export function restosToRelance(tenants: TenantPerf[], threshold = 5): TenantPerf[] {
  return tenants.filter((t) => t.orders < threshold);
}

/** Échappe un champ CSV (séparateur ;). */
function csv(field: string | number): string {
  const s = String(field);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Export Odoo CSV (parité legacy) : 1 ligne par resto, abonnement 99 € + commissions
 * (0,99 € × commandes en ligne) + TVA 20 %. Référence `LCC-{YYYY-MM}-{SLUG}`.
 */
export function buildOdooCsv(tenants: TenantPerf[], period: string, activeSlugs: Set<string>): string {
  const head = ['Reference', 'Restaurant', 'Slug', 'Abonnement_HT', 'Commissions_HT', 'Total_HT', 'TVA_20', 'Total_TTC'];
  const rows = tenants.map((t) => {
    const sub = activeSlugs.has(t.slug) ? SUBSCRIPTION_CENTS : 0;
    const commissions = t.onlineOrders * COMMISSION_CENTS;
    const ht = sub + commissions;
    const tva = Math.round(ht * 0.2);
    const ttc = ht + tva;
    const e = (c: number) => (c / 100).toFixed(2);
    return [`LCC-${period}-${t.slug.toUpperCase()}`, t.name, t.slug, e(sub), e(commissions), e(ht), e(tva), e(ttc)];
  });
  return [head, ...rows].map((r) => r.map(csv).join(';')).join('\n');
}
